// STEP 3-WMS-B PHASE 2-D · Seller → 3PL Combined Shipment Request modal.
// REUSES /seller/combined-shipments/3pl-* backend endpoints · NO direct ship.
// Seller can only request; operator must approve at /operator dashboard.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Package, X, Warehouse, Info } from 'lucide-react';
import api from '@/lib/api';

const _mapErr = (e) => {
  const s = e?.response?.status; const d = e?.response?.data?.detail;
  const str = typeof d === 'string' ? d : (d?.detail || '');
  if (s === 403) return str || '이 작업을 처리할 권한이 없습니다.';
  if (s === 409) return str || '이미 처리된 요청이거나 상태가 변경되었습니다.';
  if (s === 400) return str || '요청을 처리할 수 없습니다.';
  return str || '요청 실패';
};

const _uuid = () => (crypto?.randomUUID?.() || `cr-${Date.now()}-${Math.random().toString(36).slice(2,10)}`);

export default function Seller3PLCombinedRequestModal({ open, onClose, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);   // seller's own REQUESTED/REJECTED/CANCELLED groups
  const [busy, setBusy] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [tab, setTab] = useState('new');          // 'new' | 'my'

  const loadAll = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get('/seller/3pl-combined/candidates'),
        api.get('/seller/3pl-combined/requests'),
      ]);
      setGroups(c.data?.items || []);
      setRequests(r.data?.items || []);
    } catch (e) { toast.error(_mapErr(e)); }
  };
  useEffect(() => { if (open) { loadAll(); setSelectedGroup(null); setTab('new'); } }, [open]);
  if (!open) return null;

  const doRequest = async (g) => {
    setBusy(true);
    try {
      await api.post('/seller/3pl-combined/request', {
        seller_order_ids: g.orders.map((o) => o.seller_order_id),
        client_ref: _uuid(),
      });
      toast.success('📦 3PL 합포 요청 완료 · 물류센터 확인을 기다려주세요.');
      setSelectedGroup(null);
      await loadAll();
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  const doCancel = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/seller/3pl-combined/requests/${cid}/cancel`);
      toast.success('요청 취소 완료');
      await loadAll();
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-lg max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="seller-3pl-request-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-1"><Warehouse size={16}/> 3PL 합포 요청</h3>
          <button onClick={onClose} disabled={busy}><X size={16}/></button>
        </div>
        <div className="rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-1">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <p>3PL 배정 주문은 판매자가 직접 합포/출고할 수 없습니다. 요청 후 <b>물류센터 확인</b>이 완료되어야 합포가 확정됩니다.</p>
        </div>
        <div className="flex gap-1 text-xs">
          <button onClick={() => setTab('new')}
                   className={`flex-1 py-1.5 rounded ${tab==='new' ? 'bg-black text-white font-bold' : 'border'}`}
                   data-testid="seller-3pl-tab-new">요청 가능 ({groups.length})</button>
          <button onClick={() => setTab('my')}
                   className={`flex-1 py-1.5 rounded ${tab==='my' ? 'bg-black text-white font-bold' : 'border'}`}
                   data-testid="seller-3pl-tab-my">내 요청 ({requests.length})</button>
        </div>

        {tab === 'new' && !selectedGroup && (
          <>
            {groups.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4 text-center" data-testid="seller-3pl-empty">현재 요청 가능한 3PL 합포 후보가 없습니다.</p>
            ) : groups.map((g, i) => (
              <button key={i} onClick={() => setSelectedGroup(g)}
                       className="block w-full text-left rounded border p-2 hover:bg-neutral-50 text-xs"
                       data-testid={`seller-3pl-candidate-${i}`}>
                <p className="font-bold">📦 {g.orders[0].recipient_name} · {g.orders.length}건</p>
                <p className="text-neutral-500">{g.orders[0].phone} · {g.warehouse_id}</p>
                {g.orders.slice(0, 5).map((o) => <p key={o.seller_order_id} className="font-mono">· {o.order_number || o.seller_order_id}</p>)}
                {g.orders.length > 5 && <p className="text-neutral-500">외 {g.orders.length - 5}건</p>}
              </button>
            ))}
          </>
        )}

        {tab === 'new' && selectedGroup && (
          <>
            <div className="text-xs bg-neutral-50 p-2 rounded space-y-0.5">
              <p><b>수령인:</b> {selectedGroup.orders[0].recipient_name}</p>
              <p><b>전화:</b> {selectedGroup.orders[0].phone}</p>
              <p><b>물류센터:</b> <span className="font-mono">{selectedGroup.warehouse_id}</span></p>
              <p><b>3PL:</b> <span className="font-mono">{selectedGroup.operator_id}</span></p>
            </div>
            <div className="text-xs space-y-1">
              <p className="font-bold">포함 주문 {selectedGroup.orders.length}건</p>
              {selectedGroup.orders.map((o) => (
                <div key={o.seller_order_id} className="border rounded px-2 py-1">
                  <p className="font-mono">{o.order_number || o.seller_order_id}</p>
                  <p className="text-neutral-500">상품 {o.items_count}종</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setSelectedGroup(null)} disabled={busy} className="px-3 py-1.5 rounded border text-sm" data-testid="seller-3pl-back-btn">뒤로</button>
              <button onClick={() => doRequest(selectedGroup)} disabled={busy}
                       className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                       data-testid="seller-3pl-request-btn">
                {busy && <Loader2 size={12} className="animate-spin"/>} 📦 3PL 합포 요청
              </button>
            </div>
          </>
        )}

        {tab === 'my' && (
          <>
            {requests.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4 text-center" data-testid="seller-3pl-my-empty">내 요청이 없습니다.</p>
            ) : requests.map((r) => {
              const sh = r.shipment || {};
              return (
                <div key={r.combined_shipment_id} className="rounded border p-2 text-xs space-y-1" data-testid={`seller-3pl-request-${r.combined_shipment_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-mono">{r.combined_shipment_id}</p>
                    <span className={`px-1.5 py-0.5 rounded-full font-bold text-[10px] ${
                      r.status === 'REQUESTED' ? 'bg-amber-100 text-amber-800' :
                      r.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-800' :
                      r.status === 'SHIPPED' ? 'bg-emerald-200 text-emerald-900' :
                      r.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-neutral-100 text-neutral-700'
                    }`}>{r.status}</span>
                  </div>
                  <p>수령인 {r.recipient_snapshot?.recipient_name} · 주문 {(r.members || []).length}건</p>
                  {sh.tracking_number && (
                    <p className="font-mono">🚚 {sh.courier} · {sh.tracking_number}</p>
                  )}
                  {r.status === 'REJECTED' && (
                    <p className="text-red-700">거절: {r.reject_reason} {r.reject_note && `· ${r.reject_note}`}</p>
                  )}
                  {r.status === 'REQUESTED' && (
                    <button onClick={() => doCancel(r.combined_shipment_id)} disabled={busy}
                             className="px-2 py-1 rounded border text-[11px] text-red-700 border-red-300 disabled:opacity-50"
                             data-testid={`seller-3pl-cancel-${r.combined_shipment_id}`}>요청 취소</button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
